/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type SpringRef,
  type TransitionFn,
  type animated,
  useTransition,
} from "@react-spring/web";
import { type EventTypes, type Handler, useScroll } from "@use-gesture/react";
import {
  type CSSProperties,
  type ComponentProps,
  type ComponentType,
  type Dispatch,
  type FC,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SetStateAction,
  createContext,
  memo,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import useMeasure from "react-use-measure";
import classNames from "classnames";
import { logger } from "matrix-js-sdk/lib/logger";

import styles from "./Grid.module.css";
import { useMergedRefs } from "../useMergedRefs";
import { TileWrapper } from "./TileWrapper";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";
import { useInitial } from "../useInitial";

const MAX_ANIMATED_TILES = 50; // Capped for performance reasons

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Tile<Model> {
  id: string;
  model: Model;
  onDrag: DragCallback | undefined;
}

interface PlacedTile<Model> extends Tile<Model>, Rect {
  /**
   * The box the tile is positioned within (its slot's offset parent), in grid
   * space.
   */
  container: Rect;
  /**
   * Where the tile stacks: later slots in the layout go on top, so that a
   * PiP slot nested inside a spotlight slot is seen. Tiles' DOM order is
   * fixed, so stacking must be explicit.
   */
  zIndex: number;
}

/** The z-index of a tile while it's being dragged, above everything else. */
const dragZIndex = 1000;

interface TileSpring {
  opacity: number;
  scale: number;
  zIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TileSpringUpdate extends Partial<TileSpring> {
  from?: Partial<TileSpring>;
  reset?: boolean;
  immediate?: boolean | ((key: string) => boolean);
  delay?: (key: string) => number;
}

interface DragState {
  tileId: string;
  tileX: number;
  tileY: number;
  cursorX: number;
  cursorY: number;
  /** The tile the dragged tile was last reported to be over. */
  over: string | undefined;
}

export interface SlotProps<Model> extends Omit<
  ComponentProps<"div">,
  "onDrag"
> {
  id: string;
  model: Model;
  onDrag?: DragCallback;
  style?: CSSProperties;
  className?: string;
}

interface Offset {
  x: number;
  y: number;
}

/**
 * Gets the offset of one element relative to an ancestor.
 */
function offset(element: HTMLElement, relativeTo: Element): Offset {
  if (
    !(element.offsetParent instanceof HTMLElement) ||
    element.offsetParent === relativeTo
  ) {
    return { x: element.offsetLeft, y: element.offsetTop };
  } else {
    const o = offset(element.offsetParent, relativeTo);
    o.x += element.offsetLeft;
    o.y += element.offsetTop;
    return o;
  }
}

export type VisibleTilesCallback = (visibleTiles: number) => void;

interface LayoutContext {
  setGeneration: Dispatch<SetStateAction<number | null>>;
  setVisibleTilesCallback: Dispatch<
    SetStateAction<VisibleTilesCallback | null>
  >;
}

const LayoutContext = createContext<LayoutContext | null>(null);

function useLayoutContext(): LayoutContext {
  const context = use(LayoutContext);
  if (context === null)
    throw new Error("useUpdateLayout called outside a Grid layout context");
  return context;
}

/**
 * Enables Grid to react to layout changes. You must call this in your Layout
 * component or else Grid will not be reactive.
 */
export function useUpdateLayout(): void {
  const { setGeneration } = useLayoutContext();
  // On every render, tell Grid that the layout may have changed
  useEffect(() => setGeneration((prev) => (prev === null ? 0 : prev + 1)));
}

/**
 * Asks Grid to call a callback whenever the number of visible tiles may have
 * changed.
 */
export function useVisibleTiles(callback: VisibleTilesCallback): void {
  const { setVisibleTilesCallback } = useLayoutContext();
  useEffect(
    () => setVisibleTilesCallback(() => callback),
    [callback, setVisibleTilesCallback],
  );
  useEffect(
    () => (): void => setVisibleTilesCallback(null),
    [setVisibleTilesCallback],
  );
}

export interface LayoutProps<LayoutModel, TileModel, R extends HTMLElement> {
  ref?: Ref<R>;
  model: LayoutModel;
  /**
   * Component creating an invisible "slot" for a tile to go in.
   */
  Slot: ComponentType<SlotProps<TileModel>>;
}

export interface TileProps<Model, R extends HTMLElement> {
  ref?: Ref<R>;
  className?: string;
  style?: ComponentProps<typeof animated.div>["style"];
  /**
   * The width this tile will have once its animations have settled.
   */
  targetWidth: number;
  /**
   * The height this tile will have once its animations have settled.
   */
  targetHeight: number;
  model: Model;
}

interface Drag {
  /**
   * The X coordinate of the dragged tile in grid space.
   */
  x: number;
  /**
   * The Y coordinate of the dragged tile in grid space.
   */
  y: number;
  /**
   * The X coordinate of the dragged tile relative to its container (its
   * slot's offset parent).
   */
  containerX: number;
  /**
   * The Y coordinate of the dragged tile relative to its container.
   */
  containerY: number;
  /**
   * The size of the container.
   */
  container: { width: number; height: number };
  /**
   * The size of the dragged tile.
   */
  tile: { width: number; height: number };
  /**
   * The ID of the tile whose slot the dragged tile is over, if any.
   */
  over: string | undefined;
  /**
   * Whether this is the end of the gesture. The callback is also called
   * mid-gesture whenever the tile moves over a different tile, so that
   * layouts can reflow around it as it's dragged.
   */
  final: boolean;
}

export type DragCallback = (drag: Drag) => void;

interface LayoutMemoProps<
  LayoutModel,
  TileModel,
  R extends HTMLElement,
> extends LayoutProps<LayoutModel, TileModel, R> {
  Layout: ComponentType<LayoutProps<LayoutModel, TileModel, R>>;
}

interface Props<
  LayoutModel,
  TileModel,
  LayoutRef extends HTMLElement,
  TileRef extends HTMLElement,
> {
  /**
   * Data with which to populate the layout.
   */
  model: LayoutModel;
  /**
   * A component which creates an invisible layout grid of "slots" for tiles to
   * go in. The root element must have a data-generation attribute which
   * increments whenever the layout may have changed.
   */
  Layout: ComponentType<LayoutProps<LayoutModel, TileModel, LayoutRef>>;
  /**
   * The component used to render each tile in the layout.
   */
  Tile: ComponentType<TileProps<TileModel, TileRef>>;
  className?: string;
  style?: CSSProperties;
}

/**
 * A grid of animated tiles.
 */
export function Grid<
  LayoutModel,
  TileModel,
  LayoutRef extends HTMLElement,
  TileRef extends HTMLElement,
>({
  model,
  Layout,
  Tile,
  className,
  style,
}: Props<LayoutModel, TileModel, LayoutRef, TileRef>): ReactNode {
  // Overview: This component places tiles by rendering an invisible layout grid
  // of "slots" for tiles to go in. Once rendered, it uses the DOM API to get
  // the dimensions of each slot, feeding these numbers back into react-spring
  // to let the actual tiles move freely atop the layout.

  // To tell us when the layout has changed, the layout system increments its
  // data-generation attribute, which we watch with a MutationObserver.

  const [gridRef1, gridBounds] = useMeasure();
  const [gridRoot, gridRef2] = useState<HTMLElement | null>(null);
  const gridRef = useMergedRefs<HTMLElement>(gridRef1, gridRef2);

  const windowHeight = useSyncExternalStore(
    useCallback((onChange) => {
      window.addEventListener("resize", onChange);
      return (): void => window.removeEventListener("resize", onChange);
    }, []),
    useCallback(() => window.innerHeight, []),
  );
  const orientation = useSyncExternalStore(
    useCallback((onChange) => {
      // Support for the change event is experimental
      // https://developer.mozilla.org/en-US/docs/Web/API/Screen/change_event#browser_compatibility
      (screen as unknown as EventTarget).addEventListener?.("change", onChange);
      return (): void =>
        (screen as unknown as EventTarget).removeEventListener?.(
          "change",
          onChange,
        );
    }, []),
    useCallback(() => window.innerHeight, []),
  );

  const [layoutRoot, setLayoutRoot] = useState<HTMLElement | null>(null);
  const [generation, setGeneration] = useState<number | null>(null);
  const [visibleTilesCallback, setVisibleTilesCallback] =
    useState<VisibleTilesCallback | null>(null);
  const tiles = useInitial(() => new Map<string, Tile<TileModel>>());

  const Slot: FC<SlotProps<TileModel>> = useMemo(
    () =>
      function Slot({ id, model, onDrag, style, className, ...props }) {
        const ref = useRef<HTMLDivElement | null>(null);

        useEffect(() => {
          tiles.set(id, { id, model, onDrag });
          return (): void => void tiles.delete(id);
        }, [id, model, onDrag]);

        return (
          <div
            ref={ref}
            className={classNames(className, styles.slot)}
            data-id={id}
            style={style}
            {...props}
          />
        );
      },
    [tiles],
  );

  // We must memoize the Layout component to break the update loop where a
  // render of Grid causes a re-render of Layout, which in turn re-renders Grid
  const LayoutMemo = useMemo(
    () =>
      memo(function LayoutMemo({
        ref,
        Layout,
        ...props
      }: LayoutMemoProps<LayoutModel, TileModel, LayoutRef>): ReactNode {
        return <Layout {...props} ref={ref} />;
      }),
    [],
  );

  const context: LayoutContext = useMemo(
    () => ({ setGeneration, setVisibleTilesCallback }),
    [setVisibleTilesCallback],
  );

  // Combine the tile definitions and slots together to create placed tiles
  const placedTiles = useMemo(() => {
    const result: PlacedTile<TileModel>[] = [];

    if (gridRoot !== null && layoutRoot !== null) {
      const slots = layoutRoot.getElementsByClassName(
        styles.slot,
      ) as HTMLCollectionOf<HTMLElement>;
      for (const slot of slots) {
        const id = slot.getAttribute("data-id")!;
        if (slot.offsetWidth > 0 && slot.offsetHeight > 0) {
          const parent = slot.offsetParent;
          const container =
            parent instanceof HTMLElement && parent !== gridRoot
              ? {
                  ...offset(parent, gridRoot),
                  width: parent.offsetWidth,
                  height: parent.offsetHeight,
                }
              : {
                  x: 0,
                  y: 0,
                  width: gridBounds.width,
                  height: gridBounds.height,
                };
          result.push({
            ...tiles.get(id)!,
            ...offset(slot, gridRoot),
            width: slot.offsetWidth,
            height: slot.offsetHeight,
            container,
            zIndex: result.length + 1,
          });
        }
      }
    }

    return result;
    // The rects may change due to the grid resizing, changing orientation, or
    // updating to a new generation, but eslint can't statically verify this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRoot, layoutRoot, tiles, gridBounds, orientation, generation]);

  // The height of the portion of the grid visible at any given time
  const visibleHeight = useMemo(
    () => Math.min(gridBounds.bottom, windowHeight) - gridBounds.top,
    [gridBounds, windowHeight],
  );

  useEffect(() => {
    visibleTilesCallback?.(
      placedTiles.filter((tile) => tile.y + tile.height <= visibleHeight)
        .length,
    );
  }, [placedTiles, visibleTilesCallback, visibleHeight]);

  // Drag state is stored in a ref rather than component state, because we use
  // react-spring's imperative API during gestures to improve responsiveness
  const dragState = useRef<DragState | null>(null);
  // The latest placements, for settling a dropped tile after the layout has
  // reacted to the drop
  const placedTilesRef = useRef(placedTiles);
  placedTilesRef.current = placedTiles;

  // If true, disables animations
  const immediate =
    usePrefersReducedMotion() || placedTiles.length > MAX_ANIMATED_TILES;

  const [tileTransitions, springRef] = useTransition(
    placedTiles,
    () => ({
      key: ({ id }: Tile<TileModel>): string => id,
      from: ({
        x,
        y,
        width,
        height,
        zIndex,
      }: PlacedTile<TileModel>): TileSpringUpdate => ({
        opacity: 0,
        scale: 0,
        zIndex,
        x,
        y,
        width,
        height,
        immediate,
      }),
      enter: { opacity: 1, scale: 1, immediate },
      update: ({
        id,
        x,
        y,
        width,
        height,
        zIndex,
      }: PlacedTile<TileModel>): TileSpringUpdate | null =>
        id === dragState.current?.tileId
          ? null
          : {
              x,
              y,
              width,
              height,
              zIndex,
              immediate: immediate || ((key): boolean => key === "zIndex"),
            },
      leave: { opacity: 0, scale: 0, immediate },
      config: { mass: 0.7, tension: 252, friction: 25 },
    }),
    // react-spring's types are bugged and can't infer the spring type
  ) as unknown as [
    TransitionFn<PlacedTile<TileModel>, TileSpring>,
    SpringRef<TileSpring>,
  ];

  // Because we're using react-spring in imperative mode, we're responsible for
  // firing animations manually whenever the tiles array updates
  useEffect(() => {
    springRef.start().forEach((p) => void p.catch(logger.error));
  }, [placedTiles, springRef]);

  const animateDraggedTile = (
    endOfGesture: boolean,
    callback: DragCallback,
  ): void => {
    const { tileId, tileX, tileY } = dragState.current!;
    const tile = placedTiles.find((t) => t.id === tileId);
    // The tile can vanish mid-gesture (a reconnect can replace it, say).
    // Then there's nothing to drag any more; forget the gesture rather than
    // leaving it half-done, which would poison every later drag.
    if (tile === undefined) {
      dragState.current = null;
      return;
    }

    const controller = springRef.current.find(
      (c) => (c.item as Tile<TileModel>).id === tileId,
    );
    controller
      ?.start(
        endOfGesture
          ? {
              scale: 1,
              zIndex: tile.zIndex,
              immediate: immediate || ((key): boolean => key === "zIndex"),
              // Allow the tile's position to settle before pushing its
              // z-index back down
              delay: (key): number => (key === "zIndex" ? 500 : 0),
            }
          : {
              scale: 1.1,
              zIndex: dragZIndex,
              x: tileX,
              y: tileY,
              immediate:
                immediate ||
                ((key): boolean =>
                  key === "zIndex" || key === "x" || key === "y"),
            },
      )
      .catch(logger.error);
    if (endOfGesture) {
      // Settle the tile onto its slot, but only once the layout has had the
      // chance to react to the drop: sending it back to the slot it started
      // from first, and then on to a new one, made it bounce. Floating
      // tiles get a slot where they were dropped; a tile dropped outside
      // any slot goes home.
      requestAnimationFrame(() => {
        const settled = placedTilesRef.current.find((t) => t.id === tileId);
        if (settled === undefined) return;
        controller
          ?.start({
            x: settled.x,
            y: settled.y,
            width: settled.width,
            height: settled.height,
            immediate,
          })
          .catch(logger.error);
      });
    }

    const centerX = tileX + tile.width / 2;
    const centerY = tileY + tile.height / 2;
    let over = placedTiles.find(
      (t) =>
        t.id !== tileId &&
        centerX >= t.x &&
        centerX < t.x + t.width &&
        centerY >= t.y &&
        centerY < t.y + t.height,
    )?.id;
    if (over === undefined) {
      // Dropped in the blank space past the end of the list (below the last
      // tile, or to its right on its row): treat that as over the last tile,
      // so that the dragged tile goes to the end
      const sameContainer = (t: PlacedTile<TileModel>): boolean =>
        t.container.x === tile.container.x &&
        t.container.y === tile.container.y &&
        t.container.width === tile.container.width &&
        t.container.height === tile.container.height;
      const peers = placedTiles.filter(
        (t) => t.onDrag !== undefined && sameContainer(t),
      );
      const last = peers.at(-1);
      if (
        last !== undefined &&
        last.id !== tileId &&
        (centerY >= last.y + last.height ||
          (centerY >= last.y && centerX >= last.x + last.width))
      )
        over = last.id;
    }
    if (endOfGesture || over !== dragState.current!.over) {
      dragState.current!.over = over;
      const { container } = tile;
      callback({
        x: tileX,
        y: tileY,
        containerX: tileX - container.x,
        containerY: tileY - container.y,
        container: { width: container.width, height: container.height },
        tile: { width: tile.width, height: tile.height },
        over,
        final: endOfGesture,
      });
    }
  };

  // Callback for useDrag. We could call useDrag here, but the default
  // pattern of spreading {...bind()} across the children to bind the gesture
  // ends up breaking memoization and ruining this component's performance.
  // Instead, we pass this callback to each tile via a ref, to let them bind the
  // gesture using the much more sensible ref-based method.
  const onTileDrag = (
    tileId: string,

    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      tap,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initial: [initialX, initialY],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delta: [dx, dy],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      last,
    }: Parameters<Handler<"drag", EventTypes["drag"]>>[0],
  ): void => {
    if (!tap) {
      const tileController = springRef.current.find(
        (c) => (c.item as Tile<TileModel>).id === tileId,
      );
      const callback =
        tileController && tiles.get(tileController.item.id)?.onDrag;

      if (tileController !== undefined && callback != null) {
        // A gesture always begins with its own tile; any drag state left over
        // from another tile is stale
        if (dragState.current?.tileId !== tileId) {
          const tileSpring = tileController.get();
          dragState.current = {
            tileId,
            tileX: tileSpring.x,
            tileY: tileSpring.y,
            cursorX: initialX - gridBounds.x,
            cursorY: initialY - gridBounds.y + scrollOffset.current,
            over: undefined,
          };
        }

        dragState.current.tileX += dx;
        dragState.current.tileY += dy;
        dragState.current.cursorX += dx;
        dragState.current.cursorY += dy;

        animateDraggedTile(last, callback);
      }
    }
    if (last) dragState.current = null;
  };

  const onTileDragRef = useRef(onTileDrag);
  onTileDragRef.current = onTileDrag;

  const scrollOffset = useRef(0);

  useScroll(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ({ xy: [, y], delta: [, dy] }) => {
      scrollOffset.current = y;

      const callback =
        dragState.current && tiles.get(dragState.current.tileId)?.onDrag;
      if (dragState.current !== null && callback != null) {
        dragState.current.tileY += dy;
        dragState.current.cursorY += dy;
        animateDraggedTile(false, callback);
      }
    },
    { target: gridRoot ?? undefined },
  );

  return (
    <div
      ref={gridRef}
      className={classNames(className, styles.grid)}
      style={style}
    >
      <LayoutContext value={context}>
        <LayoutMemo
          ref={setLayoutRoot}
          Layout={Layout}
          model={model}
          Slot={Slot}
        />
      </LayoutContext>
      {
        // Render the tiles in a fixed order (by ID) rather than layout order,
        // so that reordering the layout never moves their elements in the DOM.
        // A moved element loses pointer capture, which would end a drag in
        // progress. Stacking is handled by the zIndex spring, not DOM order.
        // react-spring hands back a fragment of keyed elements.
        [
          ...(
            tileTransitions((spring, { id, model, onDrag, width, height }) => (
              <TileWrapper
                key={id}
                id={id}
                onDrag={onDrag ? onTileDragRef : null}
                targetWidth={width}
                targetHeight={height}
                model={model}
                Tile={Tile}
                {...spring}
              />
            )).props as { children: ReactElement[] }
          ).children,
          // Keys are the tiles' IDs: numeric for the call's tiles, media IDs
          // for the spotlight's, so compare them as strings
        ].sort((a, b) => String(a.key).localeCompare(String(b.key)))
      }
    </div>
  );
}
